// src/screens/Marketplace/BannerResults.js
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBanners } from '../../context/BannerContext';
import './BannerResults.css';
import api from '../../api';

// ----- Price helper (same behavior as DepartmentResults) --------------------
function getEffectivePricePi(p, piToUsdRate) {
  // 0) Prefer server-enriched fields if available
  if (typeof p?.effectivePricePi === 'number') {
    const isOnSale = !!(p?.sale?.active || p?.isOnSale === true);
    const percentOff = typeof p?.sale?.percentOff === 'number' ? p.sale.percentOff : null;
    return {
      pricePi: p.effectivePricePi,
      isOnSale,
      percentOff,
    };
  }

  // 1) Fallback compute
  const isDynamic = p?.pricingType === 'dynamic' && typeof p?.usdPrice === 'number';
  const basePi =
    isDynamic && typeof piToUsdRate === 'number' && piToUsdRate > 0
      ? p.usdPrice / piToUsdRate
      : typeof p?.price === 'number'
      ? p.price
      : null;

  const sale = p?.sale || p?.flashSale || p?.activeSale || {};
  const saleActive =
    !!sale?.active ||
    typeof p?.activeSalePricePi === 'number' ||
    typeof p?.activeSalePriceUsd === 'number' ||
    typeof p?.salePrice === 'number' ||
    typeof p?.salePricePi === 'number' ||
    typeof p?.salePriceUsd === 'number';

  let salePi = null;

  // explicit Pi first
  if (typeof sale?.pricePi === 'number') salePi = sale.pricePi;
  else if (typeof p?.activeSalePricePi === 'number') salePi = p.activeSalePricePi;
  else if (typeof p?.salePricePi === 'number') salePi = p.salePricePi;

  const usdToPi = (usd) =>
    typeof usd === 'number' && typeof piToUsdRate === 'number' && piToUsdRate > 0
      ? usd / piToUsdRate
      : null;

  if (salePi == null) {
    if (typeof sale?.priceUsd === 'number') salePi = usdToPi(sale.priceUsd);
    else if (typeof p?.activeSalePriceUsd === 'number') salePi = usdToPi(p.activeSalePriceUsd);
    else if (typeof p?.salePriceUsd === 'number') salePi = usdToPi(p.salePriceUsd);
  }

  if (salePi == null && typeof p?.salePrice === 'number') salePi = p.salePrice;

  if (basePi == null) return { pricePi: null, isOnSale: false, percentOff: null };

  if (!saleActive || salePi == null) {
    return { pricePi: basePi, isOnSale: false, percentOff: null };
  }

  const finalSalePi = Math.max(0, salePi);
  const percentOff = Math.max(0, Math.min(100, Math.round((1 - finalSalePi / basePi) * 100)));

  return {
    pricePi: finalSalePi,
    isOnSale: true,
    percentOff: Number.isFinite(percentOff) ? percentOff : null,
  };
}
// ---------------------------------------------------------------------------

export default function BannerResults({ user }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { fetchBannerProducts, banners, loadBanners, bannerCache, makeKey } = useBanners();

  // ✅ allow “sale”
  const ALLOWED_SLUGS = useMemo(
    () => new Set(['recent', 'trending', 'sponsored', 'three-day', 'under-20-pi', 'sale']),
    []
  );

const LOCAL_TITLES = useMemo(
  () => ({
    'under-20-pi': 'Under 20 Pi',
    recent: 'Discover Recent Products',
    trending: 'Trending Now',
    sponsored: 'Sponsored Picks',
    'three-day': '3-Day Delivery',
    sale: 'On Sale Now',
  }),
  []
);



  // Local hero fallback image (only for slugs we provide art for)
  const LOCAL_IMAGES = useMemo(
    () => ({
      sale: require('../../assets/sale-banner.png'),
    }),
    []
  );

  const [title, setTitle] = useState('Results');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [piToUsdRate, setPiToUsdRate] = useState(null);

  const currentBanner = banners?.find((b) => b.slug === slug);
  const username = user?.username || '';
  const limit = 12;

  // ⛳ Redirect if invalid slug
  useEffect(() => {
    if (!ALLOWED_SLUGS.has(slug)) {
      navigate('/marketplace/banner/recent', { replace: true });
    }
  }, [slug, ALLOWED_SLUGS, navigate]);

  // Preload banner list (hero text/bg)
  useEffect(() => {
    if (!banners?.length && loadBanners) loadBanners();
  }, [banners?.length, loadBanners]);

  // Cached Pi→USD rate (fallback path only)
  useEffect(() => {
    let timer;
    const fetchPiRate = async () => {
      try {
        const res = await api.get('/convert/rate');
        if (res.data?.piToUsd) setPiToUsdRate(res.data.piToUsd);
      } catch (err) {
        console.error('❌ Failed to fetch cached Pi rate:', err?.message || err);
      }
    };
    fetchPiRate();
    timer = setInterval(fetchPiRate, 600000);
    return () => clearInterval(timer);
  }, []);

  // Build a stable cache key for this view
  const cacheKey = useMemo(
    () => makeKey(slug, page, limit, username),
    [makeKey, slug, page, limit, username]
  );
  const cached = bannerCache.get(cacheKey);

  // Load page 1 on slug change
  useEffect(() => {
    // Reset view state
    setItems([]);
    setPage(1);
    setHasMore(false);
    setInitialLoaded(false);

    const keyP1 = makeKey(slug, 1, limit, username);
    const cachedP1 = bannerCache.get(keyP1);

    // If already cached, hydrate from cache (no network)
    if (cachedP1) {
      setTitle(cachedP1.title || currentBanner?.title || LOCAL_TITLES[slug] || 'Results');
      setItems(cachedP1.products || []);
      setHasMore(!!cachedP1.hasMore);
      setInitialLoaded(true);
      return;
    }

    // Otherwise fetch once
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchBannerProducts(slug, 1, limit, username);
        if (!cancelled && data) {
          setTitle(data.title || currentBanner?.title || LOCAL_TITLES[slug] || 'Results');
          setItems(data.products || []);
          setHasMore(!!data.hasMore);
          setInitialLoaded(true);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('❌ Failed loading banner products:', err?.message || err);
          const status = err?.status || err?.response?.status;
          if (status === 404) navigate('/marketplace/banner/recent', { replace: true });
          setInitialLoaded(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug, username, limit, currentBanner?.title, LOCAL_TITLES, navigate, fetchBannerProducts, bannerCache, makeKey]);

  // Load more (page > 1) — uses cache where available
  const handleLoadMore = async () => {
    if (loading || !hasMore) return;
    const nextPage = page + 1;
    const nextKey = makeKey(slug, nextPage, limit, username);
    const cachedNext = bannerCache.get(nextKey);

    setLoading(true);
    try {
      const data = cachedNext || (await fetchBannerProducts(slug, nextPage, limit, username));
      setItems((prev) => [...prev, ...(data?.products || [])]);
      setHasMore(!!data?.hasMore);
      setPage(nextPage);
    } catch (err) {
      console.error('❌ Load more failed:', err?.message || err);
    } finally {
      setLoading(false);
    }
  };

  const sevenDaysAgo = useMemo(() => new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), []);

  return (
    <div className="brx-page">
      <div className="brx-scroll">
        {/* Hero */}
        <div className="brx-hero" style={currentBanner?.bg ? { background: currentBanner.bg } : undefined}>
          {(currentBanner?.img || LOCAL_IMAGES[slug]) ? (
            <>
              <img
                className="brx-hero-img"
                src={currentBanner?.img || LOCAL_IMAGES[slug]}
                alt={currentBanner?.title || LOCAL_TITLES[slug] || 'Banner'}
              />
              <div className="brx-hero-overlay" />
            </>
          ) : (
            <div className="brx-hero-fallback" />
          )}

          <button className="brx-hero-back" onClick={() => navigate(-1)} aria-label="Go back" title="Back">
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <div className="brx-hero-text">
            <h1 className="brx-hero-title">{currentBanner?.title || title || LOCAL_TITLES[slug] || 'Results'}</h1>
            {currentBanner?.subtitle && <div className="brx-hero-sub">{currentBanner.subtitle}</div>}
          </div>
        </div>

        <div className="brx-wrapper">
          {/* Grid */}
          <div className="brx-grid">
            {!initialLoaded &&
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="brx-card brx-skeleton">
                  <div className="brx-img brx-sk" />
                  <div className="brx-name brx-sk-line brx-w-80" />
                  <div className="brx-price brx-sk-line brx-w-40" />
                </div>
              ))}

            {items.map((p) => {
              const createdAt = p?.createdAt ? new Date(p.createdAt) : null;
              const showNewBadge = createdAt && createdAt >= sevenDaysAgo;

              const { pricePi, isOnSale, percentOff } = getEffectivePricePi(p, piToUsdRate);
              const priceLabel = pricePi != null ? `@ ${pricePi.toFixed(2)} Pi` : '@ —';
              const saleBadgeText =
                typeof percentOff === 'number' && percentOff > 0 ? `-${percentOff}%` : '-SALE';

              return (
                <button
                  key={p._id}
                  className="brx-card"
                  onClick={() => navigate(`/product/${p._id}`)}
                  aria-label={`View ${p.name}`}
                >
                  <div className="brx-imgwrap">
                    <img
                      className="brx-img"
                      src={p.media?.[0] || 'https://via.placeholder.com/600x600?text=No+Image'}
                      alt={p.name}
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/600x600?text=Image';
                      }}
                    />
                    {/* Top-left sale badge */}
                    {isOnSale && <span className="brx-chip brx-chip-sale">{saleBadgeText}</span>}
                    {/* Top-right new badge */}
                    {showNewBadge && <span className="brx-chip brx-chip-new">New</span>}
                  </div>

                  <div className="brx-name" title={p.name}>
                    {p.name}
                  </div>

                  <div className="brx-meta">
                    <div className="brx-price">{priceLabel}</div>
                    {p.expectedDeliveryDays && <div className="brx-delivery">{p.expectedDeliveryDays}d</div>}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="brx-footer">
            {hasMore ? (
              <button onClick={handleLoadMore} disabled={loading} className="brx-loadmore">
                {loading ? 'Loading…' : 'Load more'}
              </button>
            ) : (
              initialLoaded && items.length > 0 && <div className="brx-endhint">You’ve reached the end</div>
            )}
            {initialLoaded && items.length === 0 && (
              <div className="brx-empty">No products match this banner yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
